import React, { useState, useMemo } from 'react';
import tournamentService, { Tournament } from '../../../services/tournamentService';
import { useToast } from '../../../hooks/useToast';
import { TournamentRoundsManager } from './TournamentRoundsManager';

interface EditTournamentProps {
  tournament: Tournament;
  onBack: () => void;
  onTournamentUpdated: (tournament: Tournament) => void;
}

export const EditTournament: React.FC<EditTournamentProps> = ({
  tournament,
  onBack,
  onTournamentUpdated
}) => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'rounds'>('details');
  const [currentTournament, setCurrentTournament] = useState(tournament);

  const handleStartTournament = async () => {
    if (!window.confirm('¿Estás seguro de que quieres iniciar este torneo? Una vez iniciado no se podrán modificar las inscripciones.')) {
      return;
    }

    try {
      setLoading(true);
      const updatedTournament = await tournamentService.startTournament(currentTournament.id);
      setCurrentTournament(updatedTournament);
      onTournamentUpdated(updatedTournament);
      success('Torneo iniciado', 'El torneo ha sido iniciado correctamente');
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar el torneo';
      showError('Error al iniciar', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEndTournament = async () => {
    if (!window.confirm('¿Estás seguro de que quieres finalizar este torneo? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setLoading(true);
      const updatedTournament = await tournamentService.endTournament(currentTournament.id);
      setCurrentTournament(updatedTournament);
      onTournamentUpdated(updatedTournament);
      success('Torneo finalizado', 'El torneo ha sido finalizado correctamente');
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al finalizar el torneo';
      showError('Error al finalizar', errorMessage);
    } finally {
      setLoading(false);
    }
  };

//   const handleUpdateTournament = async (data: UpdateTournamentData) => {
//     try {
//       setLoading(true);
//       const updatedTournament = await tournamentService.updateTournament(tournament.id, data);
//       setCurrentTournament(updatedTournament);
//       onTournamentUpdated(updatedTournament);
//       success('Torneo actualizado', 'Los cambios se han guardado correctamente');
//     } catch (err: any) {
//       const errorMessage = err instanceof Error ? err.message : 'Error al actualizar el torneo';
//       showError('Error al actualizar', errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

  const handleDeleteTournament = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este torneo? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setLoading(true);
      await tournamentService.deleteTournament(tournament.id);
      success('Torneo eliminado', 'El torneo ha sido eliminado correctamente');
      onBack();
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar el torneo';
      showError('Error al eliminar', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'details', name: 'Detalles del Torneo', icon: '📝' },
    { id: 'rounds', name: 'Rondas y Matches', icon: '⚔️' }
  ];

  // Memoizar el tournament para evitar re-renders innecesarios
  const stableTournament = useMemo(() => currentTournament, [currentTournament]);

  const getStateColor = (state?: string) => {
    switch (state) {
      case 'PROGRAMMED': return 'text-yellow-600 bg-yellow-100';
      case 'STARTED': return 'text-green-600 bg-green-100';
      case 'FINISHED': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStateText = (state?: string) => {
    switch (state) {
      case 'PROGRAMMED': return 'Programado';
      case 'STARTED': return 'En Progreso';
      case 'FINISHED': return 'Finalizado';
      default: return 'Desconocido';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            ← Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Editar: {stableTournament.name}
            </h1>
            <p className="text-gray-600">
              Gestiona los detalles y rondas del torneo
            </p>
          </div>
        </div>

        <button
          onClick={handleDeleteTournament}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          🗑️ Eliminar Torneo
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'details' | 'rounds')}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'details' && (
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Detalles del Torneo
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Nombre:</span> {stableTournament.name}
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Fecha:</span> {(() => {
                        const [year, month, day] = stableTournament.date.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        return date.toLocaleDateString('es-ES');
                      })()}
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Formato:</span> {stableTournament.format}
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Jugadores:</span> {stableTournament.current_players || 0}/{stableTournament.max_players || 0}
                    </p>
                    {stableTournament.description && (
                      <p className="text-gray-600 mb-2">
                        <span className="font-medium">Descripción:</span> {stableTournament.description}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <div className="mb-4">
                      <span className="font-medium text-gray-700">Estado del Torneo:</span>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStateColor(stableTournament.state)}`}>
                        {getStateText(stableTournament.state)}
                      </span>
                    </div>
                    
                    {/* Botones de control del torneo */}
                    <div className="space-y-3">
                      {stableTournament.state === 'PROGRAMMED' && (
                        <button
                          onClick={handleStartTournament}
                          disabled={loading}
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🚀 Iniciar Torneo
                        </button>
                      )}
                      
                      {stableTournament.state === 'STARTED' && (
                        <button
                          onClick={handleEndTournament}
                          disabled={loading}
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🏁 Finalizar Torneo
                        </button>
                      )}
                      
                      {stableTournament.state === 'FINISHED' && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            ⭐ El torneo ha finalizado
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Para editar los detalles básicos del torneo, usa el formulario de creación de torneos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rounds' && (
          <div className="p-6">
            <TournamentRoundsManager tournament={stableTournament} />
          </div>
        )}
      </div>
    </div>
  );
};
